import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { 
  Target,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Award,
  Star,
  Briefcase,
  Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Competencias = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [competencias, setCompetencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'tecnica',
    descricao: '',
    nivel_requerido: 'basico',
    categoria: ''
  });

  const tiposCompetencia = [
    { value: 'tecnica', label: 'Técnica', icon: Briefcase, color: 'blue' },
    { value: 'comportamental', label: 'Comportamental', icon: Users, color: 'green' },
    { value: 'gerencial', label: 'Gerencial', icon: Award, color: 'purple' }
  ];

  const niveisRequeridos = [
    { value: 'basico', label: 'Básico' },
    { value: 'intermediario', label: 'Intermediário' },
    { value: 'avancado', label: 'Avançado' }
  ];

  useEffect(() => {
    loadCompetencias();
  }, []);

  const clearNewParam = () => {
    if (!searchParams.get('new')) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('new');
    setSearchParams(nextParams, { replace: true });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
    clearNewParam();
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    clearNewParam();
  };

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openCreateModal();
    }
  }, [searchParams]);

  const loadCompetencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dcf/competencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompetencias(response.data);
    } catch (error) {
      console.error('Erro ao carregar competências:', error);
      // Mock data
      setCompetencias([
        {
          id: '1',
          nome: 'Excel Avançado',
          tipo: 'tecnica',
          descricao: 'Domínio avançado do Microsoft Excel incluindo fórmulas, tabelas dinâmicas e macros',
          nivel_requerido: 'avancado',
          categoria: 'Ferramentas Office',
          cargos_utilizando: 28,
          ativa: true
        },
        {
          id: '2',
          nome: 'PowerBI',
          tipo: 'tecnica',
          descricao: 'Criação de dashboards e relatórios analíticos',
          nivel_requerido: 'intermediario',
          categoria: 'Business Intelligence',
          cargos_utilizando: 12,
          ativa: true
        },
        {
          id: '3',
          nome: 'Comunicação',
          tipo: 'comportamental',
          descricao: 'Habilidade de comunicar-se de forma clara e efetiva',
          nivel_requerido: 'avancado',
          categoria: 'Interpessoal',
          cargos_utilizando: 35,
          ativa: true
        },
        {
          id: '4',
          nome: 'Liderança',
          tipo: 'comportamental',
          descricao: 'Capacidade de liderar equipes e inspirar pessoas',
          nivel_requerido: 'avancado',
          categoria: 'Liderança',
          cargos_utilizando: 15,
          ativa: true
        },
        {
          id: '5',
          nome: 'Gestão de Pessoas',
          tipo: 'gerencial',
          descricao: 'Competência para gerenciar equipes e desenvolver pessoas',
          nivel_requerido: 'avancado',
          categoria: 'Gestão',
          cargos_utilizando: 8,
          ativa: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompetencia = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/dcf/competencias`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      closeCreateModal();
      resetForm();
      loadCompetencias();
      alert('Competência criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar competência:', error);
      alert('Erro ao criar competência');
    }
  };

  const handleDeleteCompetencia = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a competência "${nome}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/dcf/competencias/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Competência excluída com sucesso!');
      loadCompetencias();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Erro ao excluir competência';
      alert(detail);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'tecnica',
      descricao: '',
      nivel_requerido: 'basico',
      categoria: ''
    });
  };

  const getTipoConfig = (tipo) => {
    switch (tipo) {
      case 'tecnica':
        return {
          ...tiposCompetencia[0],
          badgeClass: 'border-sky-200 bg-sky-100 text-sky-800',
          surfaceClass: 'bg-sky-50',
          iconWrapClass: 'bg-sky-100',
          iconClass: 'text-sky-700'
        };
      case 'comportamental':
        return {
          ...tiposCompetencia[1],
          badgeClass: 'border-emerald-200 bg-emerald-100 text-emerald-800',
          surfaceClass: 'bg-emerald-50',
          iconWrapClass: 'bg-emerald-100',
          iconClass: 'text-emerald-700'
        };
      case 'gerencial':
        return {
          ...tiposCompetencia[2],
          badgeClass: 'border-violet-200 bg-violet-100 text-violet-800',
          surfaceClass: 'bg-violet-50',
          iconWrapClass: 'bg-violet-100',
          iconClass: 'text-violet-700'
        };
      default:
        return {
          ...tiposCompetencia[0],
          badgeClass: 'border-slate-200 bg-slate-100 text-slate-800',
          surfaceClass: 'bg-slate-50',
          iconWrapClass: 'bg-slate-100',
          iconClass: 'text-slate-700'
        };
    }
  };

  const filteredCompetencias = competencias.filter(comp => {
    const matchesSearch = comp.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         comp.categoria?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = !filterTipo || comp.tipo === filterTipo;
    
    return matchesSearch && matchesTipo && comp.ativa;
  });

  const totalCompetencias = competencias.filter((comp) => comp.ativa).length;
  const tecnicasCount = competencias.filter((comp) => comp.ativa && comp.tipo === 'tecnica').length;
  const comportamentaisCount = competencias.filter((comp) => comp.ativa && comp.tipo === 'comportamental').length;
  const gerenciaisCount = competencias.filter((comp) => comp.ativa && comp.tipo === 'gerencial').length;
  const categoriasCount = new Set(competencias.filter((comp) => comp.ativa && comp.categoria).map((comp) => comp.categoria)).size;
  const cargosImpactados = competencias.reduce((sum, comp) => sum + (comp.cargos_utilizando || 0), 0);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <section className="overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900 text-white shadow-xl">
        <div className="grid gap-8 p-6 lg:grid-cols-[1.5fr_1fr] lg:p-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-cyan-100">
              <Target className="h-3.5 w-3.5" />
              Banco de Competências
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight lg:text-4xl">Competências precisam ser reutilizáveis, organizadas e vivas.</h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-200 lg:text-base">
                Esta área concentra o catálogo de competências que alimenta os cargos do DCF. O foco aqui é manter clareza por tipo, cobertura e reaproveitamento.
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <button
                type="button"
                onClick={openCreateModal}
                className="rounded-2xl bg-white px-4 py-4 text-left text-slate-900 transition-transform hover:-translate-y-0.5"
              >
                <Plus className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Nova competência</p>
                <p className="mt-1 text-sm text-slate-600">Cadastrar item técnico, comportamental ou gerencial.</p>
              </button>
              <button
                type="button"
                onClick={() => setFilterTipo('tecnica')}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left hover:bg-white/15"
              >
                <Briefcase className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Técnicas</p>
                <p className="mt-1 text-sm text-slate-200">Filtrar rapidamente as competências operacionais.</p>
              </button>
              <button
                type="button"
                onClick={() => setFilterTipo('comportamental')}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left hover:bg-white/15"
              >
                <Users className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Comportamentais</p>
                <p className="mt-1 text-sm text-slate-200">Revisar competências ligadas à cultura e postura.</p>
              </button>
              <button
                type="button"
                onClick={() => setFilterTipo('gerencial')}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 text-left hover:bg-white/15"
              >
                <Award className="h-5 w-5" />
                <p className="mt-5 text-base font-semibold">Gerenciais</p>
                <p className="mt-1 text-sm text-slate-200">Ver itens focados em liderança e gestão.</p>
              </button>
            </div>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-white/10 p-5 backdrop-blur">
            <p className="text-sm font-medium text-slate-200">Panorama do catálogo</p>
            <div className="mt-5 space-y-3 text-sm text-slate-100">
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Total de competências</span>
                <strong>{totalCompetencias}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Categorias mapeadas</span>
                <strong>{categoriasCount}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                <span>Cargos impactados</span>
                <strong>{cargosImpactados}</strong>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-emerald-300/20 px-4 py-3 text-emerald-100">
                <span>Resultado do filtro</span>
                <strong>{filteredCompetencias.length}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
          <p className="text-sm font-medium text-slate-500">Cobertura do catálogo</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{totalCompetencias}</p>
          <p className="mt-3 text-sm text-slate-600">Competências ativas disponíveis para uso nas DCFs.</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Técnicas</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{tecnicasCount}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Comportamentais</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{comportamentaisCount}</p>
        </article>
        <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Gerenciais</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{gerenciaisCount}</p>
        </article>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">Triagem</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Filtrar e revisar catálogo</h2>
            <p className="mt-2 text-sm text-slate-600">Use a busca para localizar competências e segmentar o banco por tipo.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setFilterTipo('');
            }}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Limpar filtros
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <Search className="h-4 w-4 text-slate-400" />
              Buscar por nome ou categoria
            </label>
            <input
              type="text"
              placeholder="Ex: Power BI, liderança, office..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <Filter className="h-4 w-4 text-slate-400" />
              Tipo de competência
            </label>
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">Todos os tipos</option>
              {tiposCompetencia.map(tipo => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        {filteredCompetencias.map((competencia) => {
          const tipoConfig = getTipoConfig(competencia.tipo);
          const IconComponent = tipoConfig.icon;
          
          return (
            <article key={competencia.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-0.5">
              <div className={`border-b p-5 ${tipoConfig.surfaceClass}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <div className={`rounded-2xl p-3 ${tipoConfig.iconWrapClass}`}>
                      <IconComponent className={`h-5 w-5 ${tipoConfig.iconClass}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{competencia.nome}</h3>
                      <span className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${tipoConfig.badgeClass}`}>
                        {tipoConfig.label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-1">
                    <button type="button" className="rounded-lg p-1 text-gray-300 cursor-not-allowed" title="Edição desta tela ainda não está disponível">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCompetencia(competencia.id, competencia.nome)}
                      className="rounded-lg p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-5">
                <p className="min-h-[72px] text-sm text-slate-600">{competencia.descricao}</p>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Nível</p>
                    <p className="mt-1 text-sm font-medium capitalize text-slate-900">{competencia.nivel_requerido}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Uso em cargos</p>
                    <div className="mt-1 flex items-center space-x-1 text-slate-900">
                      <Briefcase className="h-4 w-4 text-cyan-600" />
                      <span className="text-sm font-medium">{competencia.cargos_utilizando || 0} cargos</span>
                    </div>
                  </div>
                </div>

                {competencia.categoria && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Categoria</p>
                    <p className="mt-1 text-sm font-medium text-slate-700">{competencia.categoria}</p>
                  </div>
                )}
              </div>
            </article>
          );
        })}
        {filteredCompetencias.length === 0 && (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-sm">
            <Target className="mx-auto h-14 w-14 text-slate-300" />
            <p className="mt-4 text-lg font-medium text-slate-700">Nenhuma competência encontrada</p>
            <p className="mt-2 text-sm text-slate-500">Ajuste os filtros ou crie uma nova competência para alimentar o DCF.</p>
          </div>
        )}
      </section>

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-900 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.24em] text-cyan-100">Cadastro</p>
                  <h2 className="mt-2 text-2xl font-semibold">Nova competência</h2>
                  <p className="mt-1 text-sm text-slate-200">Cadastre um item reutilizável para compor cargos e jornadas de desenvolvimento.</p>
                </div>
                <button 
                  onClick={closeCreateModal}
                  className="text-white/80 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateCompetencia} className="space-y-5 p-6">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Competência*
                </label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Excel Avançado"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo*
                  </label>
                  <select
                    required
                    value={formData.tipo}
                    onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {tiposCompetencia.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nível Requerido
                  </label>
                  <select
                    value={formData.nivel_requerido}
                    onChange={(e) => setFormData({...formData, nivel_requerido: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {niveisRequeridos.map(nivel => (
                      <option key={nivel.value} value={nivel.value}>{nivel.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <input
                  type="text"
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Ferramentas Office"
                />
              </div>
              
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição*
                </label>
                <textarea
                  required
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Descreva a competência em detalhes..."
                />
              </div>

              <div className="flex justify-end space-x-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="rounded-full border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  className="rounded-full bg-cyan-600 px-4 py-2 text-white hover:bg-cyan-700 transition-colors"
                >
                  Criar Competência
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Competencias;
